import { Eye, Inbox, Plus } from "lucide-react";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import { TableSkeleton } from "@/components/ui/Skeleton";
import type { TopUpRequest, TopUpStatus } from "@/types/wallet";
import { formatDate, formatINR, formatTime, TOPUP_STATUS_META } from "../data";

interface Props {
  loading: boolean;
  topups: TopUpRequest[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (n: number) => void;
  onNewRequest: () => void;
  onViewDetail: (request: TopUpRequest) => void;
}

function Header({ onNewRequest }: { onNewRequest: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-line bg-card p-4 shadow-card">
      <h3 className="text-lg font-semibold text-ink">Top-up Requests</h3>
      <Button variant="secondary" onClick={onNewRequest} leftIcon={<Plus className="h-4 w-4" />}>
        New Request
      </Button>
    </div>
  );
}

export default function TopupsTab({
  loading,
  topups,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onNewRequest,
  onViewDetail,
}: Props) {
  const columns: Column<TopUpRequest>[] = [
    {
      key: "requestId",
      header: "Request ID",
      render: (row) => <span className="font-mono text-[13px] text-ink">{row.requestId}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => (
        <span className="whitespace-nowrap text-sm font-semibold text-ink">{formatINR(row.amount)}</span>
      ),
    },
    {
      key: "paymentMode",
      header: "Payment Mode",
      render: (row) => <span className="whitespace-nowrap text-sm text-ink">{row.paymentMode}</span>,
    },
    {
      key: "referenceNumber",
      header: "Reference Number",
      render: (row) => (
        <span className="font-mono text-[13px] text-ink-secondary">{row.referenceNumber || "—"}</span>
      ),
    },
    {
      key: "requestedAt",
      header: "Requested On",
      render: (row) => (
        <div>
          <p className="whitespace-nowrap text-[13px] text-ink">{formatDate(row.requestedAt)}</p>
          <p className="text-xs text-ink-secondary">{formatTime(row.requestedAt)}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const meta = TOPUP_STATUS_META[row.status as TopUpStatus];
        return (
          <span
            className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.bg} ${meta.text}`}
          >
            {meta.label}
          </span>
        );
      },
    },
    {
      key: "adminRemark",
      header: "Admin Remark",
      render: (row) => (
        <span className="block max-w-[220px] truncate text-[13px] text-ink-secondary">{row.adminRemark || "—"}</span>
      ),
    },
    {
      key: "action",
      header: "Actions",
      align: "right",
      render: (row) => (
        <button
          type="button"
          onClick={() => onViewDetail(row)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-primary-500 cursor-pointer"
          aria-label="View top-up details"
        >
          <Eye className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Header onNewRequest={onNewRequest} />
      {loading ? (
        <TableSkeleton rows={5} columns={7} />
      ) : topups.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No top-up requests yet"
          description="Once you request a wallet top-up it will appear here with its approval status."
          actionLabel="Request Top-up"
          onAction={onNewRequest}
        />
      ) : (
        <DataTable
          columns={columns}
          data={topups}
          rowKey={(r) => r.id}
          onRowClick={onViewDetail}
          pageSizeOptions={[10, 25, 50]}
          serverPagination={{ page, pageSize, total, onPageChange, onPageSizeChange }}
        />
      )}
    </div>
  );
}
